import React, { useCallback, useState } from 'react';
import { useOnboardingService } from '../services/onboarding.service';
import {
  type CommunicationStyle,
  INITIAL_ONBOARDING_DATA,
  type LocationType,
  ONBOARDING_STEPS,
  type OnboardingState,
  type OnboardingWizardProps,
} from '../types/onboarding.types';
import { convertSizeObjectsToRanges, shouldShowStep } from '../utils/helpers';
import { validateStepData } from '../utils/validators';

// Import step components
import CompanyInfoStep from './OnboardingSteps/CompanyInfoStep';
import EmailSettingsStep from './OnboardingSteps/EmailSettingsStep';
import IndustriesStep from './OnboardingSteps/IndustriesStep';
import IntegrationsStep from './OnboardingSteps/IntegrationsStep';
import JobTitlesStep from './OnboardingSteps/JobTitlesStep';
import LocationsStep from './OnboardingSteps/LocationsStep';
import PreviewLeads from './OnboardingSteps/PreviewLeads';
import ServicesStep from './OnboardingSteps/ServicesStep';
import SizeStep from './OnboardingSteps/SizeStep';

// import InviteUsersStep from './OnboardingSteps/InviteUsersStep';

import { useSharedAuth } from '@commander/auth';
import { Button } from '@commander/ui';
import { useTrialStatus, useUserDetails } from '@commander/user-details';
import { RiLogoutCircleRLine as LogoutIcon } from '@remixicon/react';
import { CommanderLogo3, onBoardingsvg } from '@/assets/icons/AuthIcon';
import StepNavigation from './shared/StepNavigation';
// Import shared components
import StepProgress from './shared/StepProgress';

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  onComplete,
  initialStep = 0,
  role: initialRole,
}) => {
  const onboardingService = useOnboardingService();
  const [state, setState] = useState<OnboardingState>({
    currentStep: initialStep,
    selectedRole: initialRole || null,
    isCompleted: false,
    formData: INITIAL_ONBOARDING_DATA,
    isLoading: false,
    error: null,
    isDirty: false,
    lastSaved: null,
    autoSaveEnabled: false,
  });

  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [companyProfile, setCompanyProfile] = useState<any>(null);
  const [companyICPs, setCompanyICPs] = useState<any>(null);

  const { userDetails, refetch } = useUserDetails();
  // Trials skip the integrations step. userDetails is already resolved here —
  // OnboardingPage gates this component behind its own isLoading check — so
  // isTrial is stable from first render and the step list can't shrink under a
  // live currentStep index.
  const { isTrial } = useTrialStatus();
  const { logout } = useSharedAuth();

  // Ref to ensure userDetails prefill only runs once
  const appliedUserDetailsPrefill = React.useRef(false);

  // Load company data from ICPs (runs once on mount)
  React.useEffect(() => {
    const loadCompanyData = async () => {
      try {
        const companyICPsData = await onboardingService.getCompanyICPs();
        const companyProfile = await onboardingService.getCompanyProfile();

        setCompanyProfile(companyProfile);
        setCompanyICPs(companyICPsData);

        // Extract location values from company ICPs
        const locationValues: string[] = (companyICPsData?.locations ?? [])
          .map((location: LocationType) => location?.value)
          .filter((value: string | undefined) => value && value.trim() !== '');

        const uniqueLocations = Array.from(new Set(locationValues));

        if (uniqueLocations.length > 0) {
          setState((prev) => ({
            ...prev,
            formData: {
              ...prev.formData,
              locations: uniqueLocations,
              services: companyProfile?.products || [],
              size: convertSizeObjectsToRanges(companyICPsData?.size_ranges || []),
              industries:
                companyICPsData?.business_sectors?.flatMap(
                  (sector) => sector.subsectors?.map((subsector) => subsector.name) || []
                ) || [],
              jobTitles: companyICPsData?.job_titles || [],
            },
          }));
        }
      } catch (error) {
        console.warn('Failed to load pre-filled company data:', error);
      }
    };

    loadCompanyData();
  }, [onboardingService]);

  // Prefill email settings and integrations from userDetails (runs once when available)
  React.useEffect(() => {
    if (!userDetails || appliedUserDetailsPrefill.current) return;

    appliedUserDetailsPrefill.current = true;
    setState((prev) => ({
      ...prev,
      formData: {
        ...prev.formData,
        emailSettings: {
          ...prev.formData.emailSettings,
          communicationStyle: (userDetails.user_setting?.communication_style ||
            'DEFAULT') as CommunicationStyle,
          signature: userDetails.email_signature || '',
          communicationStyleText: userDetails.user_setting?.communication_style_text || '',
        },
        integrations: [
          ...(userDetails.social_integrations?.linkedin_connected ? ['linkedin'] : []),
          ...(userDetails.social_integrations?.dynamics_connected ? ['microsoftDynamics'] : []),
          ...(userDetails.social_integrations?.calendly_connected ? ['calendly'] : []),
          ...(userDetails.social_integrations?.hubspot_connected ? ['hubspot'] : []),
          ...(userDetails.social_integrations?.pipedrive_connected ? ['pipedrive'] : []),
          ...(userDetails.social_integrations?.salesforce_connected ? ['salesforce'] : []),
          ...(userDetails.social_integrations?.twenty_connected ? ['twentyCRM'] : []),
          ...(userDetails.social_integrations?.zoho_connected ? ['zohoCRM'] : []),
        ],
      },
    }));
  }, [userDetails]);

  // Get visible steps based on user role
  const visibleSteps = React.useMemo(() => {
    return ONBOARDING_STEPS.filter((step) => shouldShowStep(step.id, state.selectedRole, isTrial));
  }, [state.selectedRole, isTrial]);

  const currentStepData = React.useMemo(() => {
    return visibleSteps[state.currentStep] || ONBOARDING_STEPS[0];
  }, [visibleSteps, state.currentStep]);

  // Validation function for current step
  const validateCurrentStep = useCallback(
    async (data: typeof state.formData): Promise<boolean> => {
      if (currentStepData?.id) {
        const validation = validateStepData(currentStepData?.id, data);
        return validation.isValid;
      }
      return false;
    },
    [currentStepData?.id]
  );

  // Save step data to API
  const saveStepData = useCallback(
    async (stepId: string, data: typeof state.formData) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      let response: any;
      const nextStepNumber = Math.min(state.currentStep + 1, visibleSteps.length);
      try {
        switch (stepId) {
          case 'company':
            response = await onboardingService.saveCompanyInfo(data, companyProfile);
            if (response?.success) {
              const res = await onboardingService.getCompanyProfile();
              setCompanyProfile(res);
            }
            break;

          case 'services':
            response = await onboardingService.saveServices(data.services, companyProfile);
            if (response?.success) {
              const res = await onboardingService.getCompanyProfile();
              setCompanyProfile(res);
            }
            break;

          case 'locations':
            response = await onboardingService.saveLocations(data.locations, companyICPs);
            if (response?.success) {
              const res = await onboardingService.getCompanyICPs();
              setCompanyICPs(res);
            }
            break;

          case 'size':
            response = await onboardingService.saveCompanySize(data.size, companyICPs);
            if (response?.success) {
              const res = await onboardingService.getCompanyICPs();
              setCompanyICPs(res);
            }
            break;

          case 'industries':
            response = await onboardingService.saveIndustries(data.industries, companyICPs);
            if (response?.success) {
              const res = await onboardingService.getCompanyICPs();
              setCompanyICPs(res);
            }
            break;

          case 'jobTitles':
            response = await onboardingService.saveJobTitles(data.jobTitles, companyICPs);
            if (response?.success) {
              const res = await onboardingService.getCompanyICPs();
              setCompanyICPs(res);
            }
            break;

          case 'emailSettings':
            // Save communication style
            response = await onboardingService.saveEmailSettings(data.emailSettings);
            if (response?.success) {
              // Also create email signature in email_signatures table
              const signatureBody = data.emailSettings.signature;
              if (signatureBody) {
                try {
                  const parsedBody =
                    typeof signatureBody === 'string' ? JSON.parse(signatureBody) : signatureBody;
                  // Only create if it's a valid Lexical JSON with root.type
                  if (parsedBody?.root?.type) {
                    await onboardingService.createEmailSignature(
                      'Default Signature',
                      parsedBody,
                      true // is_default
                    );
                  }
                } catch (error) {
                  console.warn(
                    'Failed to create email signature in email_signatures table:',
                    error
                  );
                  // Don't fail the onboarding if signature creation fails
                }
              }
              refetch();
            }
            break;

          case 'previewLeads':
            onboardingService.updateUserStep(nextStepNumber);
            break;

          case 'integrations':
            // Integrations are handled client-side for now
            onboardingService.updateUserStep(nextStepNumber);
            break;

          case 'invite':
            response = await onboardingService.saveInviteUsers(data.inviteUsers);
            if (response?.success) {
              refetch();
            }
            break;

          default:
            console.warn(`Unknown step: ${stepId}`);
        }

        if (response?.success) {
          // Update user's current onboarding step to the next step
          onboardingService.updateUserStep(nextStepNumber);
        }

        setState((prev) => ({
          ...prev,
          isLoading: false,
          lastSaved: new Date(),
          isDirty: false,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to save data',
        }));
      }
    },
    [
      onboardingService,
      companyProfile,
      companyICPs,
      refetch,
      state.currentStep,
      visibleSteps.length,
    ]
  );

  // Complete onboarding
  const completeOnboarding = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      // Mark user as having completed onboarding
      await onboardingService.completeOnboarding();

      // The shell's route guard decides `needsOnboarding` from the cached
      // user-details query, which never refetches on its own (staleTime 5m,
      // refetchOnMount/WindowFocus false). Without pulling the fresh
      // onboarding_complete=true through first, onComplete()'s navigation is
      // bounced straight back to /onboarding and the wizard remounts at
      // `step_number - 1` — the "Finish does nothing, then drops me on Email
      // Signature" bug. Await it so the guard sees the new value before we go.
      await refetch();

      setState((prev) => ({
        ...prev,
        isCompleted: true,
        isLoading: false,
      }));

      onComplete();
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to complete onboarding',
      }));
    }
  }, [onboardingService, onComplete, refetch]);

  // Navigate to next step
  const handleNext = useCallback(async () => {
    // Validate current step
    const isValid = await validateCurrentStep(state.formData);
    if (!isValid) {
      return;
    }

    // Mark current step as completed
    if (!completedSteps.includes(state.currentStep)) {
      setCompletedSteps((prev) => [...prev, state.currentStep]);
    }

    if (currentStepData?.id) {
      // Save current step data
      await saveStepData(currentStepData?.id, state.formData);
    }

    // Check if this is the last step
    if (state.currentStep === visibleSteps.length - 1) {
      await completeOnboarding();
    } else {
      // Move to next step
      const nextStep = state.currentStep + 1;
      setState((prev) => ({
        ...prev,
        currentStep: nextStep,
        isDirty: false,
      }));
    }
  }, [
    state.currentStep,
    state.formData,
    validateCurrentStep,
    saveStepData,
    currentStepData?.id,
    visibleSteps.length,
    completedSteps,
    completeOnboarding,
  ]);

  // Navigate to previous step
  const handleBack = useCallback(() => {
    if (state.currentStep > 0) {
      const prevStep = state.currentStep - 1;

      // Remove current step from completed steps
      setCompletedSteps((prev) => prev.filter((stepIndex) => stepIndex !== state.currentStep));

      setState((prev) => ({
        ...prev,
        currentStep: prevStep,
        error: null,
      }));
    }
  }, [state.currentStep]);

  // Skip current step
  const handleSkip = useCallback(async () => {
    // Check if this is the last step
    if (state.currentStep === visibleSteps.length - 1) {
      await completeOnboarding();
    } else if (state.currentStep < visibleSteps.length - 1) {
      const nextStep = state.currentStep + 1;
      setState((prev) => ({
        ...prev,
        currentStep: nextStep,
        error: null,
      }));
    }
  }, [state.currentStep, visibleSteps.length, completeOnboarding]);

  // Handle form data changes
  const handleDataChange = useCallback((updates: Partial<typeof state.formData>) => {
    setState((prev) => ({
      ...prev,
      formData: {
        ...prev.formData,
        ...updates,
      },
      isDirty: true,
      error: null,
    }));
  }, []);

  // Check if user can proceed to next step
  const canProceed = React.useMemo(() => {
    if (currentStepData?.id) {
      const validation = validateStepData(currentStepData?.id, state.formData);
      return validation.isValid;
    }
    return false;
  }, [currentStepData?.id, state.formData]);

  // Render current step component
  const renderCurrentStep = useCallback(() => {
    const commonProps = {
      data: state.formData,
      onChange: handleDataChange,
      validation: validateCurrentStep,
      onNext: handleNext,
      onBack: handleBack,
      isLoading: state.isLoading,
      error: state.error,
      selectedRole: state.selectedRole || 'salesperson',
    };

    switch (currentStepData?.id) {
      case 'company':
        return <CompanyInfoStep {...commonProps} />;
      case 'locations':
        return <LocationsStep {...commonProps} />;
      case 'services':
        return <ServicesStep {...commonProps} />;
      case 'size':
        return <SizeStep {...commonProps} />;
      case 'industries':
        return <IndustriesStep {...commonProps} />;
      case 'jobTitles':
        return <JobTitlesStep {...commonProps} />;
      case 'emailSettings':
        return <EmailSettingsStep {...commonProps} />;
      case 'previewLeads':
        return <PreviewLeads {...commonProps} />;
      case 'integrations':
        return <IntegrationsStep {...commonProps} />;
      // case 'invite':
      //   return <InviteUsersStep {...commonProps} />;
      default:
        return <div>Step not found</div>;
    }
  }, [
    currentStepData?.id,
    state.formData,
    state.isLoading,
    state.error,
    state.selectedRole,
    handleDataChange,
    validateCurrentStep,
    handleNext,
    handleBack,
  ]);

  return (
    <div className="min-h-screen bg-[#112125]">
      <div className="absolute inset-0 w-full h-full z-9">
        <div className="w-full h-full">{onBoardingsvg}</div>
      </div>
      <div className="relative min-h-screen flex items-center justify-center px-4">
        {/* Progress and Content */}
        <div className="flex max-w-6xl w-full">
          {/* Step Progress */}
          <div className="flex-shrink-0 bg-[#F0F6F6] p-[60px] rounded-l-lg">
            <StepProgress
              steps={visibleSteps}
              currentStep={state.currentStep}
              completedSteps={completedSteps}
            />
            <div className="mt-8">
              <Button
                variant="outline"
                size="lg"
                onClick={logout}
                className="w-full flex items-center justify-center gap-2 text-[#737982] px-6 py-4 rounded-full max-h-[45px] max-w-[106px] border-[#C3DBD9] bg-transparent"
              >
                <LogoutIcon className="h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>

          {/* Step Content */}
          <div className="flex-1">
            <div className="bg-white rounded-r-lg p-[64px] h-full flex flex-col justify-between">
              <div>
                {/* Step Header */}
                <div className="mb-9">
                  <div className="flex items-center justify-between">
                    <h2 className="text-3xl font-semibold text-gray-900">
                      {currentStepData?.content_title}
                    </h2>

                    <span className="fill-[#C3DBD9]">
                      <CommanderLogo3 width={27} height={25} />
                    </span>
                  </div>
                  <p className="text-gray-600 mt-1">{currentStepData?.description}</p>
                </div>

                {/* Step Content */}
                <div className="mb-8">{renderCurrentStep()}</div>
              </div>

              {/* Navigation */}
              <StepNavigation
                currentStep={state.currentStep}
                totalSteps={visibleSteps.length}
                onBack={handleBack}
                onNext={handleNext}
                onSkip={currentStepData?.isOptional ? handleSkip : undefined}
                isLoading={state.isLoading}
                canProceed={canProceed}
                nextButtonText={
                  state.currentStep === visibleSteps.length - 1 ? 'Finish' : undefined
                }
                showSkip={currentStepData?.isOptional}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
